/**
 * MFA Setup Component
 * 
 * Allows users to:
 * - Enable MFA with TOTP (Authenticator App)
 * - Enable MFA with Email OTP
 * - Enable MFA with WhatsApp OTP
 * - View and manage trusted devices
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, 
  ShieldCheck, 
  Smartphone,
  Mail, 
  MessageSquare, 
  QrCode,
  Copy,
  Check,
  Trash2,
  Monitor,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import api from '../lib/api';

const MFASetup = () => {
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [trustedDevices, setTrustedDevices] = useState([]);
  
  // Setup states
  const [setupMethod, setSetupMethod] = useState(null); // 'totp', 'email', 'whatsapp'
  const [setupData, setSetupData] = useState(null); // For TOTP: secret & URI
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Contact for email/whatsapp
  const [contact, setContact] = useState('');

  // Load MFA status
  const loadMFAStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/mfa/status');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to load MFA status:', error);
      toast.error('Failed to load MFA settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load trusted devices
  const loadTrustedDevices = useCallback(async () => {
    try {
      const response = await api.get('/mfa/devices');
      setTrustedDevices(response.data.devices || []);
    } catch (error) {
      console.error('Failed to load trusted devices:', error);
    }
  }, []);

  useEffect(() => {
    loadMFAStatus();
    loadTrustedDevices();
  }, [loadMFAStatus, loadTrustedDevices]);

  // Start MFA setup
  const handleStartSetup = async (method) => {
    try {
      setSetupMethod(method);
      setVerificationCode('');
      
      const payload = { method };
      if (method === 'email' || method === 'whatsapp') {
        if (!contact) {
          toast.error(`Please enter your ${method === 'email' ? 'email address' : 'phone number'}`);
          return;
        }
        payload.contact = contact;
      }
      
      const response = await api.post('/mfa/enable', payload);
      
      if (response.data.success) {
        setSetupData(response.data);
        
        if (method === 'totp') {
          toast.success('Scan the QR code with your authenticator app');
        } else {
          toast.success(`Verification code sent to your ${method}`);
        }
      } else {
        toast.error(response.data.error || 'Failed to start MFA setup');
        setSetupMethod(null);
      }
    } catch (error) {
      console.error('Failed to start MFA setup:', error);
      toast.error('Failed to start MFA setup');
      setSetupMethod(null);
    }
  };

  // Verify TOTP setup
  const handleVerifyTOTP = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter the 6-digit code from your authenticator app');
      return;
    }
    
    try {
      setVerifying(true);
      const response = await api.post('/mfa/totp/verify', {
        code: verificationCode,
      });
      
      if (response.data.success) {
        toast.success('MFA enabled successfully!');
        setBackupCodes(response.data.backup_codes || []);
        setShowBackupCodes(true);
        setSetupMethod(null);
        setSetupData(null);
        loadMFAStatus();
      } else {
        toast.error(response.data.error || 'Invalid code');
      }
    } catch (error) {
      console.error('Failed to verify TOTP:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Disable MFA
  const handleDisableMFA = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will make your account less secure.')) {
      return;
    }
    
    try {
      await api.post('/mfa/disable');
      toast.success('MFA disabled');
      loadMFAStatus();
    } catch (error) {
      console.error('Failed to disable MFA:', error);
      toast.error('Failed to disable MFA');
    }
  };

  // Revoke trusted device
  const handleRevokeDevice = async (deviceId) => {
    try {
      await api.delete(`/mfa/devices/${deviceId}`);
      toast.success('Device revoked');
      loadTrustedDevices();
    } catch (error) {
      console.error('Failed to revoke device:', error);
      toast.error('Failed to revoke device');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  // Generate QR code URL (using public QR API)
  const getQRCodeUrl = (uri) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mfa-setup">
      {/* MFA Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {mfaStatus?.mfa_enabled ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <Shield className="h-5 w-5 text-yellow-600" />
            )}
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">
                {mfaStatus?.mfa_enabled ? 'MFA is enabled' : 'MFA is not enabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {mfaStatus?.mfa_enabled 
                  ? `Using ${mfaStatus.primary_method === 'totp' ? 'Authenticator App' : mfaStatus.primary_method}`
                  : 'Protect your account with two-factor authentication'}
              </p>
            </div>
            {mfaStatus?.mfa_enabled && (
              <Button variant="destructive" size="sm" onClick={handleDisableMFA}>
                Disable MFA
              </Button>
            )}
          </div>

          {mfaStatus?.globally_enforced && !mfaStatus?.mfa_enabled && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">MFA Required</p>
                <p className="text-sm text-yellow-700">
                  Your administrator has enforced MFA for all accounts. 
                  Please set up two-factor authentication to continue using LetWise.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Options (only if MFA not enabled) */}
      {!mfaStatus?.mfa_enabled && (
        <Card>
          <CardHeader>
            <CardTitle>Choose Your MFA Method</CardTitle>
            <CardDescription>
              Select how you want to receive verification codes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="totp" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="totp" className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  <span className="hidden sm:inline">Authenticator</span>
                </TabsTrigger>
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="hidden sm:inline">Email</span>
                </TabsTrigger>
                <TabsTrigger value="whatsapp" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </TabsTrigger>
              </TabsList>

              {/* TOTP Setup */}
              <TabsContent value="totp" className="space-y-4 mt-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Authenticator App</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Use apps like Google Authenticator, Authy, or 1Password to generate 
                    time-based codes. This is the most secure option.
                  </p>
                  
                  {!setupData ? (
                    <Button onClick={() => handleStartSetup('totp')} data-testid="setup-totp">
                      <QrCode className="h-4 w-4 mr-2" />
                      Set Up Authenticator
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-col items-center p-4 bg-white rounded-lg">
                        <img 
                          src={getQRCodeUrl(setupData.uri)} 
                          alt="QR Code" 
                          className="w-48 h-48"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Scan this QR code with your authenticator app
                        </p>
                      </div>
                      
                      <div className="p-3 bg-muted rounded flex items-center justify-between">
                        <code className="text-sm font-mono">{setupData.secret}</code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(setupData.secret)}
                        >
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Enter the 6-digit code from your app
                        </label>
                        <div className="flex gap-2">
                          <Input
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="text-center text-2xl tracking-widest font-mono"
                            maxLength={6}
                          />
                          <Button 
                            onClick={handleVerifyTOTP} 
                            disabled={verifying || verificationCode.length < 6}
                          >
                            {verifying ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Email Setup */}
              <TabsContent value="email" className="space-y-4 mt-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Email Verification</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Receive a verification code via email each time you sign in.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Email Address</label>
                      <Input
                        type="email"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        placeholder="your@email.com"
                      />
                    </div>
                    <Button onClick={() => handleStartSetup('email')} data-testid="setup-email">
                      <Mail className="h-4 w-4 mr-2" />
                      Enable Email MFA
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* WhatsApp Setup */}
              <TabsContent value="whatsapp" className="space-y-4 mt-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">WhatsApp Verification</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Receive verification codes via WhatsApp. Make sure you have 
                    WhatsApp installed on your phone.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Phone Number</label>
                      <Input
                        type="tel"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        placeholder="+447885507742"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Include country code (e.g., +44 for UK)
                      </p>
                    </div>
                    <Button onClick={() => handleStartSetup('whatsapp')} data-testid="setup-whatsapp">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Enable WhatsApp MFA
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Trusted Devices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Trusted Devices
          </CardTitle>
          <CardDescription>
            Devices that can skip MFA verification for {mfaStatus?.remember_device_days || 60} days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {trustedDevices.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No trusted devices yet. When you verify MFA and choose &quot;Remember this device&quot;, 
              it will appear here.
            </p>
          ) : (
            <div className="space-y-3">
              {trustedDevices.map((device) => (
                <div 
                  key={device.id} 
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Monitor className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{device.device_name || 'Unknown Device'}</p>
                      <p className="text-sm text-muted-foreground">
                        {device.browser} • {device.ip_address}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Last used: {new Date(device.last_used_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRevokeDevice(device.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
            <DialogDescription>
              These codes can be used to access your account if you lose your authenticator device. 
              Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <code key={index} className="text-sm font-mono bg-background p-2 rounded text-center">
                  {code}
                </code>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => copyToClipboard(backupCodes.join('\n'))}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy All
            </Button>
            <Button onClick={() => setShowBackupCodes(false)}>
              I&apos;ve Saved These
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center">
            Store these codes in a safe place. You won&apos;t be able to see them again.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MFASetup;
